import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import { useCurrencyMaster } from '../../../hooks/useCurrencyMaster';
import { useProductDetailsMaster } from '../../../hooks/useProductDetailsMaster';
import { useFactoryDetailsMaster } from '../../../hooks/useFactoryDetailsMaster';
import '../../../assets/styles/invoice_list.scss';

export default function InvoiceList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [acceptedOrders, setAcceptedOrders] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Master Data Hooks
  const { currencyOptions } = useCurrencyMaster();
  const { attributeUnitOptions } = useProductDetailsMaster();
  const { paymentScheduleOptions, shippingTermsOptions, modeOfTransportOptions } = useFactoryDetailsMaster();

  // Fetch accepted purchase orders on component mount

  // Helper function to get unit label
  const getUnitLabel = (unitValue) => {
    if (!unitValue) return '';
    if (!attributeUnitOptions || attributeUnitOptions.length === 0) return unitValue;
    
    const unitValueNum = isNaN(unitValue) ? null : Number(unitValue);
    const foundByValue = attributeUnitOptions.find(opt => {
      if (String(opt.value) === String(unitValue)) return true;
      if (unitValueNum !== null && !isNaN(opt.value)) {
        return Number(opt.value) === unitValueNum;
      }
      return false;
    });
    
    if (foundByValue) {
      return foundByValue.label || unitValue;
    }
    
    const foundByLabel = attributeUnitOptions.find(opt => 
      opt.label === String(unitValue) || 
      opt.label?.toLowerCase() === String(unitValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return unitValue;
  };

  // Helper function to get currency symbol
  const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return '';
    if (currencyOptions && currencyOptions.length > 0) {
      const foundByCode = currencyOptions.find(opt => 
        opt.currencyCode === String(currencyCode).toUpperCase() ||
        String(opt.value) === String(currencyCode) ||
        opt.currencyCode === String(currencyCode)
      );
      if (foundByCode && foundByCode.currencySymbol) {
        return foundByCode.currencySymbol;
      }
    }
    return '';
  };

  // Helper function to fetch full PO details with RFQ data
  const fetchFullPODetails = async (order) => {
    try {
      const poId = order.id || order.purchase_order_id;
      if (!poId) return order;
      
      const originalRfqId = order.rfq_id;
      
      const fullPOResponse = await CommonService.get(`/purchase-order/${poId}`);
      if (fullPOResponse && fullPOResponse.success && fullPOResponse.data) {
        const finalRfqId = originalRfqId || fullPOResponse.data.rfq_id || null;
        
        let rfqData = null;
        if (finalRfqId) {
          try {
            const rfqIdNum = typeof finalRfqId === 'string' && finalRfqId.startsWith('REQ-')
              ? parseInt(finalRfqId.replace('REQ-', ''), 10)
              : parseInt(finalRfqId, 10);
            
            if (!isNaN(rfqIdNum)) {
              const rfqResponse = await CommonService.get(`/rfq/${rfqIdNum}`);
              if (rfqResponse && rfqResponse.success && rfqResponse.data) {
                rfqData = rfqResponse.data;
              }
            }
          } catch (rfqError) {
            console.warn(`Error fetching RFQ ${finalRfqId} for PO ${poId}:`, rfqError);
          }
        }
        
        return {
          ...order,
          ...fullPOResponse.data,
          rfq_id: finalRfqId,
          rfq_data: rfqData,
          line_items: fullPOResponse.data.line_items || fullPOResponse.data.po_data?.lineItems || [],
          po_data: {
            ...order.po_data,
            ...fullPOResponse.data.po_data,
            lineItems: fullPOResponse.data.line_items || fullPOResponse.data.po_data?.lineItems || []
          }
        };
      }
      return order;
    } catch (error) {
      console.error(`Error fetching full PO details for PO ${order.id}:`, error);
      return order;
    }
  };

  // Fetch invoices for a purchase order
  const fetchInvoicesForPO = async (poId) => {
    try {
      const response = await CommonService.get(`/seller-invoice/purchase-order/${poId}`);
      if (response && response.success && response.data) {
        return Array.isArray(response.data) ? response.data : [];
      }
      return [];
    } catch (error) {
      console.warn(`Error fetching invoices for PO ${poId}:`, error);
      return [];
    }
  };

  // Fetch accepted purchase orders
  const fetchAcceptedOrders = async () => {
    try {
      setLoading(true);
      
      const poResponse = await CommonService.get("/purchase-order/seller/my-orders");
      
      if (poResponse && poResponse.success && poResponse.data) {
        const allOrders = Array.isArray(poResponse.data) ? poResponse.data : [];
        
        // Filter only accepted orders
        const accepted = allOrders.filter(order => {
          const status = (order.po_status || '').toLowerCase();
          const responseStatus = (order.po_response_status || '').toLowerCase();
          return status === 'accepted' || responseStatus === 'accepted';
        });
        
        // Fetch full PO details (including line items and RFQ data)
        const acceptedWithDetails = await Promise.all(
          accepted.map(order => fetchFullPODetails(order))
        );
        
        // Fetch invoices for each PO
        const ordersWithInvoices = await Promise.all(
          acceptedWithDetails.map(async (order) => {
            const poId = order.id || order.purchase_order_id;
            const invoices = poId ? await fetchInvoicesForPO(poId) : [];
            return {
              ...order,
              invoices: invoices
            };
          })
        );
        
        setAcceptedOrders(ordersWithInvoices);
      } else {
        setAcceptedOrders([]);
      }
    } catch (error) {
      console.error('Error fetching accepted purchase orders:', error);
      toast.error('Failed to fetch accepted purchase orders');
      setAcceptedOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAcceptedOrders();
  }, []);

  // Transform order data for display
  const transformOrderData = (order) => {
    let poData = {};
    let totalAmount = 0;
    let lineItems = [];
    
    // Get invoice information
    const invoices = order.invoices || [];
    const latestInvoice = invoices.length > 0 
      ? invoices.sort((a, b) => new Date(b.created_datetime || b.invoice_date) - new Date(a.created_datetime || a.invoice_date))[0]
      : null;
    
    // Determine status and invoice number
    let invoiceStatus = 'Pending';
    let invoiceNumber = null;
    let invoiceId = null;
    let invoiceDate = null;
    let invoiceGrandTotal = null;
    let invoiceCurrency = null;
    
    if (latestInvoice) {
      invoiceId = latestInvoice.id;
      invoiceNumber = latestInvoice.invoice_number || null;
      invoiceDate = latestInvoice.invoice_date || null;
      invoiceGrandTotal = latestInvoice.grand_total || null;
      invoiceCurrency = latestInvoice.currency || null;
      const rawStatus = latestInvoice.status || '';
      const status = rawStatus.toLowerCase().trim();
      
      // Debug logging (can be removed in production)
      if (process.env.NODE_ENV === 'development') {
        console.log('Invoice Status Debug:', {
          invoiceId,
          invoiceNumber,
          rawStatus,
          status,
          hasInvoiceNumber: !!invoiceNumber
        });
      }
      
      // Handle all possible invoice statuses
      if (status === 'draft') {
        invoiceStatus = 'Draft';
      } else if (status === 'generated' || status === 'sent') {
        invoiceStatus = 'Invoice Created';
      } else if (status === 'accepted') {
        invoiceStatus = 'Accepted';
      } else if (status === 'disputed') {
        invoiceStatus = 'Disputed';
      } else if (status && status !== '') {
        // If status exists but doesn't match known statuses, show it as-is (capitalized)
        invoiceStatus = status.charAt(0).toUpperCase() + status.slice(1);
      } else {
        // If invoice exists but status is empty/null, check if invoice_number exists
        // If invoice_number exists, it means invoice was created, so show as "Invoice Created"
        if (invoiceNumber) {
          invoiceStatus = 'Invoice Created';
        } else {
          invoiceStatus = 'Pending';
        }
      }
    } else {
      // No invoice found - check if invoices array exists but is empty
      if (invoices && invoices.length === 0) {
        invoiceStatus = 'Pending';
      }
    }
    
    try {
      if (order.po_data) {
        poData = order.po_data;
        if (poData.lineItems && Array.isArray(poData.lineItems) && poData.lineItems.length > 0) {
          lineItems = poData.lineItems;
        }
      }
      if (order.line_items && Array.isArray(order.line_items) && order.line_items.length > 0) {
        lineItems = order.line_items;
      }
      
      if (lineItems && lineItems.length > 0) {
        totalAmount = lineItems.reduce((sum, item) => {
          if (item.total !== undefined && item.total !== null && item.total !== '') {
            const itemTotal = parseFloat(item.total);
            if (!isNaN(itemTotal) && itemTotal > 0) {
              return sum + itemTotal;
            }
          }
          
          const unitPrice = parseFloat(item.unit_price || item.unitPrice) || 0;
          const quantity = parseFloat(item.quantity) || 0;
          const discount = parseFloat(item.discount) || 0;
          const subtotal = unitPrice * quantity;
          const discountAmount = subtotal * (discount / 100);
          const taxableAmount = subtotal - discountAmount;
          
          const cgstAmount = parseFloat(item.cgst_amount || item.cgstAmount) || 0;
          const sgstAmount = parseFloat(item.sgst_amount || item.sgstAmount) || 0;
          const igstAmount = parseFloat(item.igst_amount || item.igstAmount) || 0;
          const cessAmount = parseFloat(item.cess_amount || item.cessAmount) || 0;
          const customsAmount = parseFloat(item.customs_amount || item.customsAmount) || 0;
          const bcdAmount = parseFloat(item.bcd_amount || item.bcdAmount) || 0;
          const swsAmount = parseFloat(item.sws_amount || item.swsAmount) || 0;
          const taxAmount = parseFloat(item.tax_amount || item.taxAmount) || 0;
          
          if (item.assessable_value !== undefined && item.assessable_value !== null && item.assessable_value !== '') {
            const assessableValue = parseFloat(item.assessable_value || item.assessableValue) || 0;
            const igstOnAssessable = parseFloat(item.igst_amount || item.igstAmount) || 0;
            const cessOnAssessable = parseFloat(item.cess_amount || item.cessAmount) || 0;
            return sum + (assessableValue + igstOnAssessable + cessOnAssessable);
          }
          
          return sum + (taxableAmount + cgstAmount + sgstAmount + igstAmount + cessAmount + customsAmount + bcdAmount + swsAmount + taxAmount);
        }, 0);
      }
    } catch (e) {
      console.warn('Error parsing PO data:', e);
    }
    
    // Use invoice currency if available, otherwise use PO currency
    const currency = invoiceCurrency || poData.currency || order.currency || 'INR';
    const currencyCode = getCurrencyLabel(currency);
    
    // Use invoice grand_total if available, otherwise calculate from PO line items
    const displayTotalAmount = invoiceGrandTotal !== null && invoiceGrandTotal !== undefined 
      ? invoiceGrandTotal 
      : totalAmount;
    
    // Extract product names - prioritize RFQ data
    let productNames = [];
    if (order.rfq_data && order.rfq_data.product_name) {
      const rfqProductName = order.rfq_data.product_name.trim();
      if (rfqProductName && rfqProductName !== '' && rfqProductName !== 'N/A') {
        let cleanedProductName = rfqProductName
          .replace(/\bHSN\s*[:\-]?\s*\d{4,8}\b/gi, '')
          .replace(/\b\d{4,8}\s*\(HSN\)/gi, '')
          .replace(/\bHSN\s*Code\s*[:\-]?\s*\d{4,8}\b/gi, '')
          .trim();
        cleanedProductName = cleanedProductName.replace(/\s+/g, ' ').trim();
        if (cleanedProductName && cleanedProductName !== '') {
          productNames.push(cleanedProductName);
        }
      }
    }
    
    if (productNames.length === 0 && lineItems && lineItems.length > 0) {
      lineItems.forEach(item => {
        let productName = item.description || item.productName || item.product_name || item.name || '';
        if (productName) {
          productName = productName
            .replace(/\bHSN\s*[:\-]?\s*\d{4,8}\b/gi, '')
            .replace(/\b\d{4,8}\s*\(HSN\)/gi, '')
            .replace(/\bHSN\s*Code\s*[:\-]?\s*\d{4,8}\b/gi, '')
            .trim();
          productName = productName.replace(/\s+/g, ' ').trim();
        }
        if (productName && productName.trim() !== '' && productName !== 'N/A') {
          if (!productNames.includes(productName.trim())) {
            productNames.push(productName.trim());
          }
        }
      });
    }
    
    const productNamesDisplay = productNames.length > 0 
      ? (productNames.length > 3 ? productNames.slice(0, 3).join(', ') + ` +${productNames.length - 3} more` : productNames.join(', '))
      : 'N/A';
    
    // Extract product categories
    let productCategories = [];
    if (lineItems && lineItems.length > 0) {
      lineItems.forEach(item => {
        const category = item.category || item.productCategory || item.product_category || item.category_name || '';
        if (category && category.trim() !== '' && !productCategories.includes(category.trim())) {
          productCategories.push(category.trim());
        }
      });
    }
    
    if (productCategories.length === 0) {
      const rfqData = order.rfq_data || order.rfqData;
      if (rfqData) {
        const categoryName = rfqData.category_name || rfqData.categoryName || '';
        if (categoryName && categoryName.trim() !== '') {
          productCategories.push(categoryName.trim());
        }
      }
    }
    
    const productCategoriesDisplay = productCategories.length > 0 
      ? productCategories.join(', ')
      : 'N/A';
    
    // Calculate quantity from line items
    let totalQuantity = 0;
    let quantityUnit = '';
    if (lineItems && lineItems.length > 0) {
      lineItems.forEach(item => {
        const itemQuantity = parseFloat(item.quantity) || 0;
        totalQuantity += itemQuantity;
        if (!quantityUnit && item.uom) {
          quantityUnit = getUnitLabel(item.uom);
        }
      });
    }
    
    const quantityDisplay = totalQuantity > 0 
      ? `${totalQuantity} ${quantityUnit || ''}`.trim()
      : 'N/A';
    
    return {
      id: order.id || order.purchase_order_id,
      poNumber: order.po_number || `PO-${order.id}`,
      poDate: invoiceDate ? formatDate(invoiceDate) : formatDate(order.po_date),
      productName: productNamesDisplay,
      category: productCategoriesDisplay,
      quantity: quantityDisplay,
      totalAmount: formatCurrency(displayTotalAmount, currencyCode),
      expectedDelivery: poData.expectedDeliveryDate ? formatDate(poData.expectedDeliveryDate) : (order.expected_delivery_date ? formatDate(order.expected_delivery_date) : 'N/A'),
      invoiceStatus: invoiceStatus,
      invoiceNumber: invoiceNumber,
      invoiceId: invoiceId,
      originalData: order
    };
  };

  // Helper function to get status badge class
  const getStatusBadgeClass = (status) => {
    if (!status) return "il-status-badge il-status-pending";
    
    const statusLower = status.toLowerCase().trim();
    switch (statusLower) {
      case "pending":
        return "il-status-badge il-status-pending";
      case "draft":
        return "il-status-badge il-status-draft";
      case "invoice created":
      case "generated":
      case "sent":
        return "il-status-badge il-status-invoice-created";
      case "accepted":
        return "il-status-badge il-status-accepted";
      case "disputed":
        return "il-status-badge il-status-disputed";
      default:
        return "il-status-badge il-status-pending";
    }
  };

  // Helper function to get currency label
  const getCurrencyLabel = (currencyValue) => {
    if (!currencyValue) return 'INR';
    if (!currencyOptions || currencyOptions.length === 0) return currencyValue;
    
    const foundByValue = currencyOptions.find(opt => 
      String(opt.value) === String(currencyValue) ||
      opt.currencyCode === String(currencyValue)
    );
    if (foundByValue) {
      return foundByValue.currencyCode || foundByValue.label || currencyValue;
    }
    
    const foundByCode = currencyOptions.find(opt => 
      opt.currencyCode === String(currencyValue).toUpperCase()
    );
    if (foundByCode) {
      return foundByCode.currencyCode || foundByCode.label || currencyValue;
    }
    
    return currencyValue;
  };

  const formatCurrency = (amount, currencyCode) => {
    if (!amount && amount !== 0) return 'N/A';
    
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return 'N/A';
    
    const formatted = numAmount.toLocaleString('en-IN', {
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
    
    const symbol = getCurrencySymbol(currencyCode);
    
    return `${symbol}${formatted}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
    const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to get currency code from currency ID/value
  const getCurrencyCode = (currencyValue) => {
    if (!currencyValue) return 'INR';
    if (!currencyOptions || currencyOptions.length === 0) {
      const upperValue = String(currencyValue).toUpperCase();
      if (['INR', 'USD', 'EUR', 'GBP'].includes(upperValue)) {
        return upperValue;
      }
      return 'INR';
    }
    
    const foundByValue = currencyOptions.find(opt => 
      String(opt.value) === String(currencyValue) ||
      String(opt.id) === String(currencyValue)
    );
    if (foundByValue) {
      return foundByValue.currencyCode || foundByValue.label || 'INR';
    }
    
    const foundByCode = currencyOptions.find(opt => 
      opt.currencyCode === String(currencyValue).toUpperCase()
    );
    if (foundByCode) {
      return foundByCode.currencyCode || 'INR';
    }
    
    const upperValue = String(currencyValue).toUpperCase();
    if (['INR', 'USD', 'EUR', 'GBP'].includes(upperValue)) {
      return upperValue;
    }
    
    return 'INR';
  };

  // Helper function to get payment terms label
  const getPaymentTermsLabel = (paymentTermsValue) => {
    if (!paymentTermsValue) return '';
    if (!paymentScheduleOptions || paymentScheduleOptions.length === 0) {
      return String(paymentTermsValue);
    }
    
    const foundByValue = paymentScheduleOptions.find(opt => 
      String(opt.value) === String(paymentTermsValue) ||
      String(opt.id) === String(paymentTermsValue)
    );
    if (foundByValue) {
      return foundByValue.label || String(paymentTermsValue);
    }
    
    const foundByLabel = paymentScheduleOptions.find(opt => 
      opt.label === String(paymentTermsValue) ||
      opt.label?.toLowerCase() === String(paymentTermsValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return String(paymentTermsValue);
  };

  // Helper function to get delivery terms label
  const getDeliveryTermsLabel = (deliveryTermsValue) => {
    if (!deliveryTermsValue) return '';
    if (!shippingTermsOptions || shippingTermsOptions.length === 0) {
      return String(deliveryTermsValue);
    }
    
    const foundByValue = shippingTermsOptions.find(opt => 
      String(opt.value) === String(deliveryTermsValue) ||
      String(opt.id) === String(deliveryTermsValue)
    );
    if (foundByValue) {
      return foundByValue.label || String(deliveryTermsValue);
    }
    
    const foundByLabel = shippingTermsOptions.find(opt => 
      opt.label === String(deliveryTermsValue) ||
      opt.label?.toLowerCase() === String(deliveryTermsValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return String(deliveryTermsValue);
  };

  // Helper function to get shipping method label
  const getShippingMethodLabel = (shippingMethodValue) => {
    if (!shippingMethodValue) return '';
    if (!modeOfTransportOptions || modeOfTransportOptions.length === 0) {
      return String(shippingMethodValue);
    }
    
    const foundByValue = modeOfTransportOptions.find(opt => 
      String(opt.value) === String(shippingMethodValue) ||
      String(opt.id) === String(shippingMethodValue)
    );
    if (foundByValue) {
      return foundByValue.label || String(shippingMethodValue);
    }
    
    const foundByLabel = modeOfTransportOptions.find(opt => 
      opt.label === String(shippingMethodValue) ||
      opt.label?.toLowerCase() === String(shippingMethodValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return String(shippingMethodValue);
  };

  // Get detailed invoice data from API
  const getInvoiceDetails = async (invoiceId) => {
    try {
      const response = await CommonService.get(`/seller-invoice/${invoiceId}`);
      
      if (response && response.success && response.data) {
        const invoice = response.data;
        
        const currencyValue = invoice.currency || 'INR';
        const currencyCode = getCurrencyCode(currencyValue);
        const grandTotal = invoice.grand_total || 0;
        const formattedAmount = formatCurrency(grandTotal, currencyValue);
        
        const tradeType = invoice.trade_type || '';
        const tradeTypeLower = (tradeType || '').toLowerCase();
        
        const isIntraState = tradeTypeLower.includes('intra-state') || 
                            (tradeTypeLower.includes('cgst') && tradeTypeLower.includes('sgst'));
        const isImport = tradeTypeLower.includes('import') || 
                        tradeTypeLower.includes('cif') || 
                        tradeTypeLower.includes('bcd');
        const isThirdCountry = tradeTypeLower.includes('third country');
        const isExport = tradeTypeLower.includes('export') || 
                        tradeTypeLower.includes('lut');
        const isInterState = tradeTypeLower.includes('inter-state') || 
                            (tradeTypeLower.includes('igst') && !isExport && !isImport && !isIntraState);
        
        const transformedInvoice = {
          id: invoice.id,
          invoiceNumber: invoice.invoice_number || '',
          invoiceDate: invoice.invoice_date || '',
          dueDate: invoice.due_date || '',
          poNumber: invoice.po_reference || invoice.po_number || '',
          currency: currencyCode,
          currencyValue: currencyValue,
          amount: formattedAmount,
          status: invoice.status === 'Generated' || invoice.status === 'Sent' 
            ? 'Invoice Created' 
            : (invoice.status === 'Accepted' ? 'Accepted' : invoice.status || 'Pending'),
          tradeType: tradeType,
          isIntraState: isIntraState,
          isImport: isImport,
          isThirdCountry: isThirdCountry,
          isExport: isExport,
          isInterState: isInterState,
          seller: {
            companyName: invoice.seller_company_name || '',
            gstin: invoice.seller_gstin || '',
            address: Array.isArray(invoice.seller_address) 
              ? invoice.seller_address.join(', ') 
              : (typeof invoice.seller_address === 'string' ? invoice.seller_address : ''),
            email: invoice.seller_email || '',
            phone: invoice.seller_phone || ''
          },
          buyer: {
            companyName: invoice.buyer_company_name || '',
            gstin: invoice.buyer_tax_id || 'N/A (International)',
            address: Array.isArray(invoice.buyer_address) 
              ? invoice.buyer_address.join(', ') 
              : (typeof invoice.buyer_address === 'string' ? invoice.buyer_address : '')
          },
          items: (invoice.line_items || []).map(item => {
            const currencyValue = invoice.currency || 'INR';
            const currencyCode = getCurrencyCode(currencyValue);
            const symbol = getCurrencySymbol(currencyCode);
            
            const unitPrice = parseFloat(item.unit_price) || 0;
            const quantity = parseFloat(item.quantity) || 0;
            const discount = parseFloat(item.discount) || 0;
            
            const itemSubtotal = quantity * unitPrice;
            const discountAmount = itemSubtotal * (discount / 100);
            const taxableAmount = itemSubtotal - discountAmount;
            const totalAmount = parseFloat(item.total_amount) || 0;
            
            let cgst = 0;
            let sgst = 0;
            let igst = 0;
            let cess = 0;
            let customsDuty = 0;
            let tax = 0;
            
            if (isIntraState) {
              const cgstPercent = parseFloat(item.cgst) || 0;
              const sgstPercent = parseFloat(item.sgst) || 0;
              const cessPercent = parseFloat(item.cess) || 0;
              cgst = taxableAmount * (cgstPercent / 100);
              sgst = taxableAmount * (sgstPercent / 100);
              cess = taxableAmount * (cessPercent / 100);
            } else if (isImport) {
              const customsDutyPercent = parseFloat(item.customs_duty) || 0;
              const igstPercent = parseFloat(item.igst) || 0;
              const cessPercent = parseFloat(item.cess) || 0;
              customsDuty = taxableAmount * (customsDutyPercent / 100);
              const amountAfterCustoms = taxableAmount + customsDuty;
              igst = amountAfterCustoms * (igstPercent / 100);
              cess = amountAfterCustoms * (cessPercent / 100);
            } else if (isThirdCountry) {
              const taxPercent = parseFloat(item.tax) || 0;
              tax = taxableAmount * (taxPercent / 100);
            } else {
              const igstPercent = parseFloat(item.igst) || 0;
              const cessPercent = parseFloat(item.cess) || 0;
              igst = taxableAmount * (igstPercent / 100);
              cess = taxableAmount * (cessPercent / 100);
            }
            
            const unitLabel = getUnitLabel(item.unit);
            
            return {
              sku: item.hsn_sac || 'N/A',
              description: item.description || '',
              hsn: item.hsn_sac || '',
              quantity: `${quantity} ${unitLabel}`,
              unitPrice: `${symbol} ${unitPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              discount: `${symbol} ${discountAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              taxableAmount: `${symbol} ${taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              cgst: `${symbol} ${cgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              sgst: `${symbol} ${sgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              igst: `${symbol} ${igst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              cess: `${symbol} ${cess.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              customsDuty: `${symbol} ${customsDuty.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              tax: `${symbol} ${tax.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              total: `${symbol} ${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            };
          }),
          paymentTerms: {
            paymentTerms: getPaymentTermsLabel(invoice.payment_terms || ''),
            deliveryTerms: getDeliveryTermsLabel(invoice.trade_terms || ''),
            placeOfSupply: invoice.place_of_supply || '',
            shippingMethod: getShippingMethodLabel(invoice.shipping_method || '')
          },
          amountSummary: (() => {
            let totalIGST = 0;
            let totalCESS = 0;
            let totalCGST = 0;
            let totalSGST = 0;
            let totalCustomsDuty = 0;
            let totalTax = 0;
            let calculatedSubtotal = 0;
            let calculatedDiscount = 0;
            
            (invoice.line_items || []).forEach(item => {
              const qty = parseFloat(item.quantity) || 0;
              const unitPrice = parseFloat(item.unit_price) || 0;
              const itemSubtotal = qty * unitPrice;
              calculatedSubtotal += itemSubtotal;
              
              const discount = parseFloat(item.discount) || 0;
              const discountAmount = itemSubtotal * (discount / 100);
              calculatedDiscount += discountAmount;
              
              const taxableAmount = itemSubtotal - discountAmount;
              
              if (isIntraState) {
                const cgstPercent = parseFloat(item.cgst) || 0;
                const sgstPercent = parseFloat(item.sgst) || 0;
                const cessPercent = parseFloat(item.cess) || 0;
                totalCGST += taxableAmount * (cgstPercent / 100);
                totalSGST += taxableAmount * (sgstPercent / 100);
                totalCESS += taxableAmount * (cessPercent / 100);
              } else if (isImport) {
                const customsDutyPercent = parseFloat(item.customs_duty) || 0;
                const igstPercent = parseFloat(item.igst) || 0;
                const cessPercent = parseFloat(item.cess) || 0;
                totalCustomsDuty += taxableAmount * (customsDutyPercent / 100);
                const amountAfterCustoms = taxableAmount + (taxableAmount * (customsDutyPercent / 100));
                totalIGST += amountAfterCustoms * (igstPercent / 100);
                totalCESS += amountAfterCustoms * (cessPercent / 100);
              } else if (isThirdCountry) {
                const taxPercent = parseFloat(item.tax) || 0;
                totalTax += taxableAmount * (taxPercent / 100);
              } else {
                const igstPercent = parseFloat(item.igst) || 0;
                const cessPercent = parseFloat(item.cess) || 0;
                totalIGST += taxableAmount * (igstPercent / 100);
                totalCESS += taxableAmount * (cessPercent / 100);
              }
            });
            
            const subtotal = invoice.subtotal || calculatedSubtotal;
            const totalDiscount = invoice.total_discount || calculatedDiscount;
            const fobValue = invoice.fob_value || (subtotal - totalDiscount);
            const grandTotal = invoice.grand_total || 0;
            
            const currencyCode = getCurrencyCode(invoice.currency || 'INR');
            const symbol = getCurrencySymbol(currencyCode);
            const discountFormatted = totalDiscount > 0 
              ? `- ${symbol} ${totalDiscount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : `${symbol} 0.00`;
            
            return {
              subtotal: formatCurrency(subtotal, invoice.currency || 'INR'),
              subtotalRaw: subtotal,
              totalDiscount: discountFormatted,
              totalDiscountRaw: totalDiscount,
              taxableAmount: formatCurrency(fobValue, invoice.currency || 'INR'),
              taxableAmountRaw: fobValue,
              cgst: formatCurrency(totalCGST, invoice.currency || 'INR'),
              cgstRaw: totalCGST,
              sgst: formatCurrency(totalSGST, invoice.currency || 'INR'),
              sgstRaw: totalSGST,
              igst: formatCurrency(totalIGST, invoice.currency || 'INR'),
              igstRaw: totalIGST,
              cess: formatCurrency(totalCESS, invoice.currency || 'INR'),
              cessRaw: totalCESS,
              customsDuty: formatCurrency(totalCustomsDuty, invoice.currency || 'INR'),
              customsDutyRaw: totalCustomsDuty,
              tax: formatCurrency(totalTax, invoice.currency || 'INR'),
              taxRaw: totalTax,
              grandTotal: formatCurrency(grandTotal, invoice.currency || 'INR'),
              grandTotalRaw: grandTotal
            };
          })(),
          bankDetails: {
            bankName: invoice.bank_name_1 || '',
            accountNumber: invoice.account_number_1 || '',
            ifsc: invoice.swift_ifsc_1 || invoice.iban_1 || ''
          },
          packagingInfo: {
            noOfPackages: invoice.no_of_packages || null,
            packagingType: invoice.packaging_type || '',
            grossWeight: invoice.gross_weight || null,
            netWeight: invoice.net_weight || null,
            dimensions: invoice.dimensions || ''
          },
          additionalNotes: invoice.additional_notes || '',
          acceptanceInfo: invoice.status === 'Accepted' ? {
            decision: invoice.acceptance_decision || 'ACCEPT',
            acceptedByName: invoice.accepted_by_name || '',
            acceptedByDesignation: invoice.accepted_by_designation || '',
            acceptanceDate: invoice.acceptance_date || '',
            paymentCommitmentDate: invoice.payment_commitment_date || '',
            acceptanceNotes: invoice.acceptance_notes || '',
            acceptanceRemarks: invoice.acceptance_remarks || '',
            acceptedAt: invoice.accepted_at || ''
          } : null
        };
        
        return transformedInvoice;
      }
      return null;
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      toast.error('Failed to fetch invoice details');
      return null;
    }
  };

  const handleViewInvoice = async (invoiceId) => {
    const invoiceDetails = await getInvoiceDetails(invoiceId);
    if (invoiceDetails) {
      setSelectedInvoice(invoiceDetails);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInvoice(null);
  };

  // Filter orders by search query
  const filteredOrders = acceptedOrders.filter(order => {
    if (!searchQuery) return true;
    const transformed = transformOrderData(order);
    const query = searchQuery.toLowerCase();
    return (
      (transformed.poNumber && transformed.poNumber.toLowerCase().includes(query)) ||
      (transformed.productName && transformed.productName.toLowerCase().includes(query)) ||
      (transformed.category && transformed.category.toLowerCase().includes(query))
    );
  });

  return (
    <div className="invoice-list">
      <div className="il-main-content">
        {/* Header Section */}
        <div className="il-header">
          <div className="il-header-left">
            <nav aria-label="breadcrumb" className="il-breadcrumb">
              <ol className="il-breadcrumb-list">
                <li className="il-breadcrumb-item">
                  <Link to="/dashboard/seller" className="il-breadcrumb-link">Home</Link>
                </li>
                <li className="il-breadcrumb-item il-breadcrumb-active" aria-current="page">
                  Create and Manage Invoices
                </li>
              </ol>
            </nav>
            <h1 className="il-main-title">Create and Manage Invoices</h1>
            <p className="il-subtitle">Create and manage invoices for accepted purchase orders</p>
          </div>
          <div className="il-header-right">
            <button className="il-back-btn" onClick={() => navigate(-1)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m12 19-7-7 7-7"></path>
                <path d="M19 12H5"></path>
              </svg>
              Back
            </button>
          </div>
        </div>

        {/* Search Section */}
        <div className="il-search-filter-section">
          <div className="il-search-header">
            <h2 className="il-search-title">Search Purchase Orders</h2>
          </div>
          <div className="il-search-filter-row">
            <div className="il-search-wrapper">
              <svg 
                className="il-search-icon" 
                xmlns="http://www.w3.org/2000/svg" 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="il-search-input"
                placeholder="Search by PO number, product name, or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Purchase Order Cards */}
        <div className="il-invoices-container">
          {loading ? (
            <div className="il-empty-state">
              <p>Loading accepted purchase orders...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="il-empty-state">
              <p>No accepted purchase orders found.</p>
            </div>
          ) : (
            filteredOrders.map(order => {
              const transformed = transformOrderData(order);
              return (
                <div key={transformed.id} className="il-invoice-card">
                <div className="il-card-content-wrapper">
                  <div className="il-card-main-row">
                    <div className="il-card-left-section">
                      <div className="il-invoice-header-row">
                        <div className="il-invoice-header-left">
                        <svg 
                          className="il-document-icon"
                          xmlns="http://www.w3.org/2000/svg" 
                          width="24" 
                          height="24" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                          <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                          <path d="M10 9H8"></path>
                          <path d="M16 13H8"></path>
                          <path d="M16 17H8"></path>
                        </svg>
                        <div className="il-invoice-title-group">
                              <div className="il-title-row">
                                <h3 className="il-invoice-number">
                                  {transformed.invoiceNumber || transformed.poNumber}
                                </h3>
                                <span className={getStatusBadgeClass(transformed.invoiceStatus)}>
                                  {transformed.invoiceStatus ? transformed.invoiceStatus.charAt(0).toUpperCase() + transformed.invoiceStatus.slice(1).toLowerCase() : 'Pending'}
                                </span>
                              </div>
                              {transformed.invoiceNumber && (
                                <p className="il-invoice-number-subtitle">
                                  PO: <span className="il-invoice-number-value">{transformed.poNumber}</span>
                                </p>
                              )}
                          </div>
                        </div>
                        <div className="il-card-actions">
                            {/* Show Generate Invoice button only for Pending status */}
                            {transformed.invoiceStatus === 'Pending' && (
                          <button 
                                className="il-action-btn il-btn-generate"
                                onClick={() => navigate('/generate-invoice', { 
                                  state: { 
                                    poId: transformed.id,
                                    purchaseOrder: transformed.originalData 
                                  } 
                                })}
                          >
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              width="16" 
                              height="16" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            >
                                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                                  <polyline points="14 2 14 8 20 8"></polyline>
                                  <line x1="12" y1="18" x2="12" y2="12"></line>
                                  <line x1="9" y1="15" x2="15" y2="15"></line>
                            </svg>
                                Generate Invoice
                          </button>
                            )}
                            
                            {/* Show Edit Invoice button for Draft status */}
                            {transformed.invoiceStatus === 'Draft' && transformed.invoiceId && (
                          <button 
                                className="il-action-btn il-btn-edit"
                                onClick={() => navigate('/generate-invoice', { 
                                  state: { 
                                    poId: transformed.id,
                                    invoiceId: transformed.invoiceId,
                                    purchaseOrder: transformed.originalData 
                                  } 
                                })}
                          >
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              width="16" 
                              height="16" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            >
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                                Edit Invoice
                            </button>
                          )}

                          {/* Show View Invoice button for Invoice Created status */}
                          {transformed.invoiceStatus === 'Invoice Created' && transformed.invoiceId && (
                            <button 
                              className="il-action-btn il-btn-view"
                              onClick={() => handleViewInvoice(transformed.invoiceId)}
                            >
                              <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                width="16" 
                                height="16" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                              >
                                <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                              View Invoice
                            </button>
                          )}

                          {/* Show View Accepted Invoice Details button for Accepted status */}
                          {transformed.invoiceStatus === 'Accepted' && transformed.invoiceId && (
                            <button 
                              className="il-action-btn il-btn-view-accepted"
                              onClick={() => handleViewInvoice(transformed.invoiceId)}
                            >
                              <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                width="16" 
                                height="16" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                              >
                                <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                              View Accepted Invoice Details
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="il-details-grid">
                        <div className="il-grid-item">
                            <p className="il-detail-label">{transformed.invoiceNumber ? 'Invoice Date' : 'PO Date'}</p>
                            <p className="il-detail-value">{transformed.poDate}</p>
                        </div>
                        <div className="il-grid-item">
                            <p className="il-detail-label">Product Name</p>
                            <p className="il-detail-value" title={transformed.productName}>{transformed.productName}</p>
                        </div>
                        <div className="il-grid-item">
                            <p className="il-detail-label">Category</p>
                            <p className="il-detail-value">{transformed.category}</p>
                        </div>
                        <div className="il-grid-item">
                            <p className="il-detail-label">Quantity</p>
                            <p className="il-detail-value">{transformed.quantity}</p>
                        </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">Total Amount</p>
                            <p className="il-detail-value">{transformed.totalAmount}</p>
                          </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">Expected Delivery Date</p>
                            <p className="il-detail-value">{transformed.expectedDelivery}</p>
                          </div>
                        </div>
                    </div>
                  </div>
                </div>
              </div>
              );
            })
          )}
        </div>
      </div>

      {/* Invoice Details Modal */}
      {isModalOpen && selectedInvoice && (
        <div className="il-modal-overlay" onClick={handleCloseModal}>
          <div
            className="il-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="il-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="il-modal-close" 
              onClick={handleCloseModal}
              aria-label="Close invoice details"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>

            <div className="il-modal-header">
              <h2 id="il-modal-title" className="il-modal-title">
                {selectedInvoice.status === 'Accepted' ? 'Accepted Invoice Details' : 'Invoice Details'} - {selectedInvoice.invoiceNumber}
              </h2>
              <p className="il-modal-subtitle">Complete invoice information with all items and tax details</p>
            </div>

            <div className="il-modal-content">
              {/* Summary Grid */}
              <div className="il-summary-grid">
                <div className="il-summary-item">
                  <label className="il-summary-label">Invoice Number</label>
                  <p className="il-summary-value">{selectedInvoice.invoiceNumber}</p>
                </div>
                <div className="il-summary-item">
                  <label className="il-summary-label">Invoice Date</label>
                  <p className="il-summary-value">{formatDateForDisplay(selectedInvoice.invoiceDate)}</p>
                </div>
                <div className="il-summary-item">
                  <label className="il-summary-label">Due Date</label>
                  <p className="il-summary-value">{formatDateForDisplay(selectedInvoice.dueDate)}</p>
                </div>
                <div className="il-summary-item">
                  <label className="il-summary-label">PO Number</label>
                  <p className="il-summary-value">{selectedInvoice.poNumber}</p>
                </div>
                <div className="il-summary-item">
                  <label className="il-summary-label">Currency</label>
                  <p className="il-summary-value">{selectedInvoice.currency}</p>
                </div>
                <div className="il-summary-item">
                  <label className="il-summary-label">Status</label>
                  <span className={getStatusBadgeClass(selectedInvoice.status)}>
                    {selectedInvoice.status}
                  </span>
                </div>
              </div>

              {/* Seller Details */}
              <div className="il-modal-section">
                <h3 className="il-section-title">Seller / Supplier Details</h3>
                <div className="il-section-card">
                  <div className="il-section-grid">
                    <div>
                      <label className="il-section-label">Company Name</label>
                      <p className="il-section-value">{selectedInvoice.seller.companyName}</p>
                    </div>
                    <div>
                      <label className="il-section-label">GSTIN</label>
                      <p className="il-section-value">{selectedInvoice.seller.gstin}</p>
                    </div>
                    <div>
                      <label className="il-section-label">Email</label>
                      <p className="il-section-value">{selectedInvoice.seller.email || '—'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">Phone</label>
                      <p className="il-section-value">{selectedInvoice.seller.phone || '—'}</p>
                    </div>
                    <div style={{ gridColumn: 'span 4' }}>
                      <label className="il-section-label">Address</label>
                      <p className="il-section-value">{selectedInvoice.seller.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Buyer Details */}
              <div className="il-modal-section">
                <h3 className="il-section-title">Buyer Details</h3>
                <div className="il-section-card">
                  <div className="il-section-grid">
                    <div>
                      <label className="il-section-label">Company Name</label>
                      <p className="il-section-value">{selectedInvoice.buyer.companyName}</p>
                    </div>
                    <div>
                      <label className="il-section-label">GSTIN</label>
                      <p className="il-section-value">{selectedInvoice.buyer.gstin}</p>
                    </div>
                    <div style={{ gridColumn: 'span 4' }}>
                      <label className="il-section-label">Address</label>
                      <p className="il-section-value">{selectedInvoice.buyer.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Invoice Items */}
              <div className="il-modal-section">
                <h3 className="il-section-title">Invoice Items</h3>
                <div className="il-table-wrapper">
                  <table className="il-table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Description</th>
                        <th>HSN</th>
                        <th className="il-text-right">Qty</th>
                        <th className="il-text-right">Unit Price</th>
                        <th className="il-text-right">Discount</th>
                        <th className="il-text-right">Taxable Amt</th>
                        {selectedInvoice.isIntraState ? (
                          <>
                            <th className="il-text-right">CGST</th>
                            <th className="il-text-right">SGST</th>
                            <th className="il-text-right">CESS</th>
                          </>
                        ) : selectedInvoice.isImport ? (
                          <>
                            <th className="il-text-right">Customs Duty</th>
                            <th className="il-text-right">IGST</th>
                            <th className="il-text-right">CESS</th>
                          </>
                        ) : selectedInvoice.isThirdCountry ? (
                          <th className="il-text-right">Tax</th>
                        ) : (
                          <>
                            <th className="il-text-right">IGST</th>
                            <th className="il-text-right">CESS</th>
                          </>
                        )}
                        <th className="il-text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.items.map((item, index) => (
                        <tr key={index}>
                          <td>{item.sku}</td>
                          <td>{item.description}</td>
                          <td>{item.hsn}</td>
                          <td className="il-text-right">{item.quantity}</td>
                          <td className="il-text-right">{item.unitPrice}</td>
                          <td className="il-text-right">{item.discount}</td>
                          <td className="il-text-right">{item.taxableAmount}</td>
                          {selectedInvoice.isIntraState ? (
                            <>
                              <td className="il-text-right">{item.cgst}</td>
                              <td className="il-text-right">{item.sgst}</td>
                              <td className="il-text-right">{item.cess}</td>
                            </>
                          ) : selectedInvoice.isImport ? (
                            <>
                              <td className="il-text-right">{item.customsDuty}</td>
                              <td className="il-text-right">{item.igst}</td>
                              <td className="il-text-right">{item.cess}</td>
                            </>
                          ) : selectedInvoice.isThirdCountry ? (
                            <td className="il-text-right">{item.tax}</td>
                          ) : (
                            <>
                              <td className="il-text-right">{item.igst}</td>
                              <td className="il-text-right">{item.cess}</td>
                            </>
                          )}
                          <td className="il-text-right">{item.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Amount Summary */}
              <div className="il-modal-section">
                <h3 className="il-section-title">Amount Summary</h3>
                <div className="il-section-card">
                  <div className="il-amount-summary">
                    <div className="il-amount-row">
                      <span className="il-amount-label">Subtotal:</span>
                      <span className="il-amount-value">{selectedInvoice.amountSummary.subtotal}</span>
                    </div>
                    <div className="il-amount-row">
                      <span className="il-amount-label">Total Discount:</span>
                      <span className="il-amount-value">{selectedInvoice.amountSummary.totalDiscount}</span>
                    </div>
                    <div className="il-amount-row">
                      <span className="il-amount-label">Taxable Amount:</span>
                      <span className="il-amount-value">{selectedInvoice.amountSummary.taxableAmount}</span>
                    </div>
                    <div className="il-amount-divider"></div>
                    {selectedInvoice.isIntraState ? (
                      <>
                        {(selectedInvoice.amountSummary.cgstRaw || 0) > 0 && (
                          <div className="il-amount-row">
                            <span className="il-amount-label">CGST:</span>
                            <span className="il-amount-value">{selectedInvoice.amountSummary.cgst}</span>
                          </div>
                        )}
                        {(selectedInvoice.amountSummary.sgstRaw || 0) > 0 && (
                          <div className="il-amount-row">
                            <span className="il-amount-label">SGST:</span>
                            <span className="il-amount-value">{selectedInvoice.amountSummary.sgst}</span>
                          </div>
                        )}
                        {(selectedInvoice.amountSummary.cessRaw || 0) > 0 && (
                          <div className="il-amount-row">
                            <span className="il-amount-label">CESS:</span>
                            <span className="il-amount-value">{selectedInvoice.amountSummary.cess}</span>
                          </div>
                        )}
                      </>
                    ) : selectedInvoice.isImport ? (
                      <>
                        {(selectedInvoice.amountSummary.customsDutyRaw || 0) > 0 && (
                          <div className="il-amount-row">
                            <span className="il-amount-label">Customs Duty:</span>
                            <span className="il-amount-value">{selectedInvoice.amountSummary.customsDuty}</span>
                          </div>
                        )}
                        {(selectedInvoice.amountSummary.igstRaw || 0) > 0 && (
                          <div className="il-amount-row">
                            <span className="il-amount-label">IGST:</span>
                            <span className="il-amount-value">{selectedInvoice.amountSummary.igst}</span>
                          </div>
                        )}
                        {(selectedInvoice.amountSummary.cessRaw || 0) > 0 && (
                          <div className="il-amount-row">
                            <span className="il-amount-label">CESS:</span>
                            <span className="il-amount-value">{selectedInvoice.amountSummary.cess}</span>
                          </div>
                        )}
                      </>
                    ) : selectedInvoice.isThirdCountry ? (
                      <>
                        {(selectedInvoice.amountSummary.taxRaw || 0) > 0 && (
                          <div className="il-amount-row">
                            <span className="il-amount-label">Tax:</span>
                            <span className="il-amount-value">{selectedInvoice.amountSummary.tax}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="il-amount-row">
                          <span className="il-amount-label">IGST:</span>
                          <span className="il-amount-value">
                            {selectedInvoice.amountSummary.igst || formatCurrency(0, selectedInvoice.currencyValue || 'INR')}
                          </span>
                        </div>
                        <div className="il-amount-row">
                          <span className="il-amount-label">CESS:</span>
                          <span className="il-amount-value">
                            {selectedInvoice.amountSummary.cess || formatCurrency(0, selectedInvoice.currencyValue || 'INR')}
                          </span>
                        </div>
                      </>
                    )}
                    <div className="il-amount-divider"></div>
                    <div className="il-amount-row il-amount-total">
                      <span className="il-amount-label">Grand Total:</span>
                      <span className="il-amount-value">{selectedInvoice.amountSummary.grandTotal}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment & Delivery Terms */}
              <div className="il-modal-section">
                <h3 className="il-section-title">Payment & Delivery Terms</h3>
                <div className="il-section-card">
                  <div className="il-section-grid">
                    <div>
                      <label className="il-section-label">Payment Terms</label>
                      <p className="il-section-value">{selectedInvoice.paymentTerms.paymentTerms}</p>
                    </div>
                    <div>
                      <label className="il-section-label">Delivery Terms</label>
                      <p className="il-section-value">{selectedInvoice.paymentTerms.deliveryTerms}</p>
                    </div>
                    <div>
                      <label className="il-section-label">Place of Supply</label>
                      <p className="il-section-value">{selectedInvoice.paymentTerms.placeOfSupply}</p>
                    </div>
                    <div>
                      <label className="il-section-label">Shipping Method</label>
                      <p className="il-section-value">{selectedInvoice.paymentTerms.shippingMethod}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Packaging Information */}
              {(selectedInvoice.packagingInfo?.noOfPackages || 
                selectedInvoice.packagingInfo?.packagingType || 
                selectedInvoice.packagingInfo?.grossWeight || 
                selectedInvoice.packagingInfo?.netWeight || 
                selectedInvoice.packagingInfo?.dimensions) && (
                <div className="il-modal-section">
                  <h3 className="il-section-title">Packaging Information</h3>
                  <div className="il-section-card">
                    <div className="il-section-grid">
                      {selectedInvoice.packagingInfo.noOfPackages && (
                        <div>
                          <label className="il-section-label">No. of Packages</label>
                          <p className="il-section-value">{selectedInvoice.packagingInfo.noOfPackages}</p>
                        </div>
                      )}
                      {selectedInvoice.packagingInfo.packagingType && (
                        <div>
                          <label className="il-section-label">Packaging Type</label>
                          <p className="il-section-value">{selectedInvoice.packagingInfo.packagingType}</p>
                        </div>
                      )}
                      {selectedInvoice.packagingInfo.grossWeight && (
                        <div>
                          <label className="il-section-label">Gross Weight</label>
                          <p className="il-section-value">{selectedInvoice.packagingInfo.grossWeight} kg</p>
                        </div>
                      )}
                      {selectedInvoice.packagingInfo.netWeight && (
                        <div>
                          <label className="il-section-label">Net Weight</label>
                          <p className="il-section-value">{selectedInvoice.packagingInfo.netWeight} kg</p>
                        </div>
                      )}
                      {selectedInvoice.packagingInfo.dimensions && (
                        <div>
                          <label className="il-section-label">Dimensions</label>
                          <p className="il-section-value">{selectedInvoice.packagingInfo.dimensions}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Bank Details */}
              <div className="il-modal-section">
                <h3 className="il-section-title">Bank Details for Payment</h3>
                <div className="il-section-card">
                  <div className="il-section-grid">
                    <div>
                      <label className="il-section-label">Bank Name</label>
                      <p className="il-section-value">{selectedInvoice.bankDetails.bankName}</p>
                    </div>
                    <div>
                      <label className="il-section-label">Account Number</label>
                      <p className="il-section-value">{selectedInvoice.bankDetails.accountNumber}</p>
                    </div>
                    <div>
                      <label className="il-section-label">IFSC / SWIFT Code</label>
                      <p className="il-section-value">{selectedInvoice.bankDetails.ifsc}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              {selectedInvoice.additionalNotes && (
                <div className="il-modal-section">
                  <h3 className="il-section-title">Additional Notes</h3>
                  <div className="il-section-card">
                    <p className="il-section-value">{selectedInvoice.additionalNotes}</p>
                  </div>
                </div>
              )}

              {/* Acceptance Information - Show only for accepted invoices */}
              {selectedInvoice.status === 'Accepted' && selectedInvoice.acceptanceInfo && (
                <div className="il-modal-section">
                  <h3 className="il-section-title">Acceptance Information</h3>
                  <div className="il-section-card">
                    <div className="il-section-grid">
                      <div>
                        <label className="il-section-label">Accepted By</label>
                        <p className="il-section-value">{selectedInvoice.acceptanceInfo.acceptedByName || '—'}</p>
                      </div>
                      <div>
                        <label className="il-section-label">Designation</label>
                        <p className="il-section-value">{selectedInvoice.acceptanceInfo.acceptedByDesignation || '—'}</p>
                      </div>
                      <div>
                        <label className="il-section-label">Acceptance Date</label>
                        <p className="il-section-value">{selectedInvoice.acceptanceInfo.acceptanceDate ? formatDateForDisplay(selectedInvoice.acceptanceInfo.acceptanceDate) : '—'}</p>
                      </div>
                      <div>
                        <label className="il-section-label">Payment Commitment Date</label>
                        <p className="il-section-value">{selectedInvoice.acceptanceInfo.paymentCommitmentDate ? formatDateForDisplay(selectedInvoice.acceptanceInfo.paymentCommitmentDate) : '—'}</p>
                      </div>
                      {selectedInvoice.acceptanceInfo.acceptanceNotes && (
                        <div style={{ gridColumn: 'span 4' }}>
                          <label className="il-section-label">Acceptance Notes</label>
                          <p className="il-section-value">{selectedInvoice.acceptanceInfo.acceptanceNotes}</p>
                        </div>
                      )}
                      {selectedInvoice.acceptanceInfo.acceptanceRemarks && (
                        <div style={{ gridColumn: 'span 4' }}>
                          <label className="il-section-label">Additional Remarks</label>
                          <p className="il-section-value">{selectedInvoice.acceptanceInfo.acceptanceRemarks}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="il-modal-footer">
              <button 
                className="il-btn il-btn-secondary"
                onClick={handleCloseModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

